<script setup lang="ts">
/**
 * Acting / Impersonation 中の常時表示バナー (#623)。
 *
 * `useActingStore.isActing` が真のときのみ画面上部に sticky で表示される。
 * - subject の名前 + ID
 * - 残り時間カウントダウン (TTL ベース)
 * - 即時 stop ボタン
 *
 * セッション切れ (TTL 0 まで) は自動で `stopActing()` を呼んで store をクリアする。
 */
import { useActing } from '~/composables/api/useActing'

const store = useActingStore()
const { stopActing } = useActing()

// Reactive countdown — re-rendered every second.
const now = ref(Math.floor(Date.now() / 1000))
let interval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  interval = setInterval(() => {
    now.value = Math.floor(Date.now() / 1000)
  }, 1000)
})
onUnmounted(() => {
  if (interval !== null) clearInterval(interval)
})

const remainingSec = computed(() => {
  if (!store.expiresAt) return 0
  return Math.max(0, store.expiresAt - now.value)
})

const remainingLabel = computed(() => {
  const s = remainingSec.value
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
})

// Auto-clear when TTL expired.
watch(remainingSec, (sec) => {
  if (sec === 0 && store.isActing) {
    store.clear()
  }
})

const onStop = async () => {
  try {
    await stopActing()
  }
  catch (e) {
    console.error('[ActingBanner] stop failed', e)
    // Keep the banner visible — the user can retry. We don't fall back to clear()
    // because that would hide the banner while the backend still has an active session.
    return
  }
  // Hard navigate to /admin/audit to fully rebuild pinia / SSR state from the
  // real user's perspective (#629). Without this, gameState / privilege / pot etc.
  // remain populated with the subject's data until the next page load — which leads
  // to confusing mismatches as new requests now resolve to the real user.
  // Industry-standard pattern (Salesforce, Okta, Datadog all do hard reload on
  // impersonation end). SPA navigation alone is insufficient.
  if (import.meta.client) {
    window.location.assign('/admin/audit')
  }
}
</script>

<template>
  <div
    v-if="store.isActing"
    class="acting-banner"
    role="alert"
    aria-live="assertive"
    data-testid="acting-banner"
  >
    <div class="acting-banner__icon">
      ⚠
    </div>
    <div class="acting-banner__text">
      <strong>Acting:</strong>
      あなたは
      <span data-testid="acting-banner-subject-name">
        {{ store.subjectName ?? '名前不明' }}
      </span>
      <span class="acting-banner__userid">
        ({{ store.subjectUserId?.slice(0, 8) ?? '' }}…)
      </span>
      として操作しています。
    </div>
    <div
      class="acting-banner__countdown"
      data-testid="acting-banner-countdown"
    >
      残り {{ remainingLabel }}
    </div>
    <button
      class="acting-banner__stop"
      data-testid="acting-banner-stop"
      type="button"
      @click="onStop"
    >
      Stop
    </button>
  </div>
</template>

<style scoped>
.acting-banner {
  position: sticky;
  top: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: linear-gradient(90deg, #b71c1c 0%, #d32f2f 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  animation: acting-banner-pulse 2s ease-in-out infinite;
}

.acting-banner__icon {
  font-size: 18px;
}

.acting-banner__text {
  flex: 1;
}

.acting-banner__userid {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 12px;
  opacity: 0.85;
  margin-left: 4px;
}

.acting-banner__countdown {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.25);
  padding: 4px 10px;
  border-radius: 4px;
}

.acting-banner__stop {
  background: #fff;
  color: #b71c1c;
  border: 0;
  border-radius: 4px;
  padding: 6px 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.acting-banner__stop:hover {
  background: #ffebee;
}

@keyframes acting-banner-pulse {
  0%,
  100% {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  }

  50% {
    box-shadow: 0 2px 16px rgba(244, 67, 54, 0.55);
  }
}
</style>
